import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IODataResponse } from '../../../core/models/odata.models';
import { ApiService } from '../../../core/services/api.service';
import { HttpService } from '../../../core/services/http.service';
import { AddressType, ICustomerAddress, ICustomerAddressFilterOptions } from '../models/customer-address.models';
import { IBankAccount, IBaseCustomerBilling, ICustomerBilling } from '../models/customer-billing.models';
import { IAvatarResponse, IBaseCustomerContact, ICustomerContact } from '../models/customer-contact.models';
import { IDocument } from '../models/customer-document.models';
import { ICustomer, ICustomerResponseDto } from '../models/customer.models';

@Injectable({ providedIn: 'root' })
export class CustomersService {
    private readonly httpService = inject(HttpService);

    private readonly apiService = inject(ApiService);

    getCustomers(): Observable<ICustomer[]> {
        return this.httpService
            .get$<IODataResponse<ICustomer>>(this.apiService.pathUrl.customers('?$expand=legalAddress'))
            .pipe(map(response => response.value));
    }

    getCustomersByIds(customerIds: string[]): Observable<ICustomer[]> {
        if (customerIds.length === 0) {
            return of([]);
        }

        const uniqueIds = [...new Set(customerIds)];
        const filter = uniqueIds.map(id => `(id eq ${id})`).join(' or ');
        const params = this.apiService.params({
            $select: 'id,customerName',
            $filter: filter,
        });
        return this.httpService
            .get$<IODataResponse<ICustomer>>(this.apiService.pathUrl.customers(`?${params.toString()}`))
            .pipe(map(response => response.value));
    }

    getCustomerById(id: string): Observable<ICustomerResponseDto> {
        return this.httpService.get$<ICustomerResponseDto>(this.apiService.pathUrl.customerById(id));
    }

    createCustomer(payload: Partial<ICustomer>): Observable<ICustomer> {
        return this.httpService.post$<ICustomer>(this.apiService.pathUrl.customers(), payload as ICustomer, new HttpParams());
    }

    updateCustomer(id: string, payload: Partial<ICustomer | ICustomerResponseDto>): Observable<ICustomer> {
        const data = { ...payload };
        delete data.version;
        delete data.id;

        const params = this.apiService.params({ version: payload.version });
        const paramsString = params.toString();
        const url = paramsString ? `${this.apiService.pathUrl.customerById(id)}?${paramsString}` : this.apiService.pathUrl.customerById(id);
        return this.httpService.put$<ICustomer>(url, data);
    }

    deleteCustomer(id: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerById(id));
    }

    // Customer Avatar Management
    getCustomerAvatar(customerId: string): Observable<Blob> {
        return this.httpService.getBlob$(this.apiService.pathUrl.customerAvatar(customerId));
    }

    getCustomerAvatarWithETag(customerId: string): Observable<{ blob: Blob; etag: string | null }> {
        return this.httpService.getBlobWithHeaders$(this.apiService.pathUrl.customerAvatar(customerId)).pipe(
            map((response: HttpResponse<Blob>) => {
                // Note: ETag header must be exposed via Access-Control-Expose-Headers: ETag on the server
                // Angular's HttpHeaders.get() is case-insensitive, so 'ETag' should work regardless of case
                const etag: string | null = response.headers.get('ETag');

                // If not found, ETag is not exposed by CORS
                // The server must include Access-Control-Expose-Headers: ETag in the response
                // Without ETag, uploads will work but without optimistic concurrency control
                if (!etag) {
                    const allHeaders: Record<string, string> = {};
                    response.headers.keys().forEach(key => {
                        allHeaders[key] = response.headers.get(key) || '';
                    });
                }

                // Remove quotes from ETag if present (ETag values are often quoted)
                const cleanEtag = etag ? etag.replaceAll('"', '') : null;

                return {
                    blob: response.body!,
                    etag: cleanEtag,
                };
            })
        );
    }

    updateCustomerAvatar(customerId: string, avatarFile: File, version?: string): Observable<IAvatarResponse> {
        const formData = new FormData();
        formData.append('image', avatarFile);
        // Format version with quotes as expected by API: version="ETag" -> version=%22ETag%22
        const versionValue = version ? `"${version}"` : undefined;
        const params = versionValue ? this.apiService.params({ version: versionValue }) : new HttpParams();
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerAvatar(customerId)}?${paramsString}`
            : this.apiService.pathUrl.customerAvatar(customerId);
        return this.httpService.put$<IAvatarResponse>(url, formData);
    }

    deleteCustomerAvatar(customerId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerAvatar(customerId));
    }

    getCustomerProjectReference(customerId: string): Observable<string[]> {
        return this.httpService.get$<string[]>(this.apiService.pathUrl.customerProjectReference(customerId));
    }

    // Customer Contacts Management
    getCustomerContacts(customerId: string): Observable<ICustomerContact[]> {
        return this.httpService
            .get$<IODataResponse<ICustomerContact>>(this.apiService.pathUrl.customerContacts(customerId))
            .pipe(map(response => response.value));
    }

    getCustomerContactById(customerId: string, contactId: string): Observable<ICustomerContact> {
        return this.httpService.get$<ICustomerContact>(this.apiService.pathUrl.customerContactById(customerId, contactId));
    }

    createCustomerContact(customerId: string, contact: IBaseCustomerContact): Observable<ICustomerContact> {
        return this.httpService.post$<ICustomerContact>(this.apiService.pathUrl.customerContacts(customerId), contact, new HttpParams());
    }

    updateCustomerContact(customerId: string, contactId: string, contact: ICustomerContact): Observable<ICustomerContact> {
        const data = { ...contact };
        delete data.version;
        delete data.id;
        const params = contact.version ? this.apiService.params({ version: contact.version }) : new HttpParams();
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerContactById(customerId, contactId)}?${paramsString}`
            : this.apiService.pathUrl.customerContactById(customerId, contactId);
        return this.httpService.put$<ICustomerContact>(url, data);
    }

    deleteCustomerContact(customerId: string, contactId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerContactById(customerId, contactId));
    }

    // Customer Contact Avatar Management
    getCustomerContactAvatar(customerId: string, contactId: string): Observable<IAvatarResponse> {
        return this.httpService.get$<IAvatarResponse>(this.apiService.pathUrl.customerContactAvatar(customerId, contactId));
    }

    updateCustomerContactAvatar(customerId: string, contactId: string, avatarFile: File): Observable<IAvatarResponse> {
        const formData = new FormData();
        formData.append('file', avatarFile);
        return this.httpService.put$<IAvatarResponse>(this.apiService.pathUrl.customerContactAvatar(customerId, contactId), formData);
    }

    deleteCustomerContactAvatar(customerId: string, contactId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerContactAvatar(customerId, contactId));
    }

    // Customer Addresses Management
    getCustomerAddresses(customerId: string, options?: ICustomerAddressFilterOptions): Observable<ICustomerAddress[]> {
        let url = this.apiService.pathUrl.customerAddresses(customerId);
        const filter = this.buildCustomerAddressTypeFilter(options);

        if (filter) {
            const params = this.apiService.params({ $filter: filter });
            const paramsString = params.toString();
            url = paramsString ? `${url}?${paramsString}` : url;
        }

        return this.httpService.get$<IODataResponse<ICustomerAddress>>(url).pipe(map(response => response.value));
    }

    getCustomerAddressesByType(customerId: string, types: AddressType[]): Observable<ICustomerAddress[]> {
        return this.getCustomerAddresses(customerId, { includeTypes: types });
    }

    private buildCustomerAddressTypeFilter(options?: ICustomerAddressFilterOptions): string | null {
        if (options === undefined) {
            return `type ne '${AddressType.Legal}'`;
        }

        const parts: string[] = [];
        const includeTypes = options.includeTypes?.filter(Boolean);
        const excludeTypes = options.excludeTypes?.filter(Boolean);

        if (includeTypes?.length) {
            const includeFilter = includeTypes.map(type => `type eq '${type}'`).join(' or ');
            parts.push(`(${includeFilter})`);
        }

        if (excludeTypes?.length) {
            parts.push(excludeTypes.map(type => `type ne '${type}'`).join(' and '));
        }

        return parts.length ? parts.join(' and ') : null;
    }

    getCustomerAddressById(customerId: string, addressId: string): Observable<ICustomerAddress> {
        return this.httpService.get$<ICustomerAddress>(this.apiService.pathUrl.customerAddressById(customerId, addressId));
    }

    createCustomerAddress(customerId: string, address: Partial<ICustomerAddress>): Observable<ICustomerAddress> {
        return this.httpService.post$<ICustomerAddress>(
            this.apiService.pathUrl.customerAddresses(customerId),
            address as object,
            new HttpParams()
        );
    }

    updateCustomerAddress(customerId: string, addressId: string, address: Partial<ICustomerAddress>): Observable<ICustomerAddress> {
        const data = { ...address };
        delete data.version;
        delete data.id;
        const params = address.version ? this.apiService.params({ version: address.version }) : new HttpParams();
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerAddressById(customerId, addressId)}?${paramsString}`
            : this.apiService.pathUrl.customerAddressById(customerId, addressId);
        return this.httpService.put$<ICustomerAddress>(url, data);
    }

    deleteCustomerAddress(customerId: string, addressId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerAddressById(customerId, addressId));
    }

    // Customer Bank Accounts Management
    getCustomerBankAccounts(customerId: string): Observable<IBankAccount[]> {
        return this.httpService
            .get$<IODataResponse<IBankAccount>>(this.apiService.pathUrl.customerBankAccounts(customerId))
            .pipe(map(response => response.value));
    }

    getCustomerBankAccountById(customerId: string, bankAccountId: string): Observable<IBankAccount> {
        return this.httpService.get$<IBankAccount>(this.apiService.pathUrl.customerBankAccountById(customerId, bankAccountId));
    }

    createCustomerBankAccount(customerId: string, bankAccount: IBankAccount): Observable<IBankAccount> {
        if (bankAccount.bic === '' || !bankAccount.bic) {
            delete bankAccount.bic;
        }
        const payload = { ...bankAccount };
        delete payload.id;
        delete payload.version;
        return this.httpService.post$<IBankAccount>(this.apiService.pathUrl.customerBankAccounts(customerId), payload, new HttpParams());
    }

    updateCustomerBankAccount(customerId: string, bankAccountId: string, bankAccount: IBankAccount): Observable<IBankAccount> {
        const payload = { ...bankAccount };
        delete payload.id;
        delete payload.version;
        const params = bankAccount.version ? this.apiService.params({ version: bankAccount.version }) : new HttpParams();
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerBankAccountById(customerId, bankAccountId)}?${paramsString}`
            : this.apiService.pathUrl.customerBankAccountById(customerId, bankAccountId);
        return this.httpService.put$<IBankAccount>(url, payload);
    }

    deleteCustomerBankAccount(customerId: string, bankAccountId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerBankAccountById(customerId, bankAccountId));
    }

    // Customer Documents Management
    getCustomerDocuments(customerId: string): Observable<IDocument[]> {
        return this.httpService.get$<IODataResponse<IDocument>>(this.apiService.pathUrl.documents('customers', customerId)).pipe(
            map(response => response.value),
            catchError((error: unknown) => {
                if (typeof error === 'object' && error !== null && 'status' in error && (error as { status?: number }).status === 404) {
                    return of([] as IDocument[]);
                }
                throw error;
            })
        );
    }

    getCustomerDocumentById(customerId: string, documentId: string): Observable<IDocument> {
        return this.httpService.get$<IDocument>(this.apiService.pathUrl.documentById('customers', customerId, documentId));
    }

    createCustomerDocument(customerId: string, file: File): Observable<IDocument> {
        const formData = new FormData();
        // Backend expects the file field to be named 'file'
        formData.append('file', file);
        return this.httpService.post$<IDocument>(this.apiService.pathUrl.documents('customers', customerId), formData, new HttpParams());
    }

    getCustomerDocumentContent(customerId: string, documentId: string): Observable<Blob> {
        return this.httpService.getBlob$(this.apiService.pathUrl.documentContent('customers', customerId, documentId));
    }

    deleteCustomerDocument(customerId: string, documentId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.documentById('customers', customerId, documentId));
    }

    downloadCustomerDocument(customerId: string, documentId: string, filename: string | null): Observable<void> {
        return this.getCustomerDocumentContent(customerId, documentId).pipe(
            map(blob => {
                const url = globalThis.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename || 'document';
                document.body.appendChild(link);
                link.click();
                link.remove();
                globalThis.URL.revokeObjectURL(url);
            })
        );
    }

    // Customer Billing Management
    getCustomerBilling(customerId: string): Observable<ICustomerBilling> {
        return this.httpService.get$<ICustomerBilling>(this.apiService.pathUrl.customerBilling(customerId));
    }

    updateCustomerBilling(customerId: string, billing: Partial<ICustomerBilling>): Observable<ICustomerBilling> {
        if (!billing?.version) {
            throw new Error('Billing version is required to update customer billing.');
        }

        const { version, ...rest } = billing;
        const billingData = rest as Partial<IBaseCustomerBilling>;
        const payload: Partial<IBaseCustomerBilling> = {};

        if (billingData.currency !== undefined) payload.currency = billingData.currency;
        if (billingData.paymentTerms !== undefined) payload.paymentTerms = billingData.paymentTerms;
        if (billingData.buyerReferenceNo !== undefined) payload.buyerReferenceNo = billingData.buyerReferenceNo;
        if (billingData.taxNumber !== undefined) payload.taxNumber = billingData.taxNumber;
        if (billingData.vatId !== undefined) payload.vatId = billingData.vatId;
        if (billingData.vatExemptionStatus !== undefined) payload.vatExemptionStatus = billingData.vatExemptionStatus;

        const params = this.apiService.params({ version });
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerBilling(customerId)}?${paramsString}`
            : this.apiService.pathUrl.customerBilling(customerId);
        return this.httpService.put$<ICustomerBilling>(url, payload);
    }

    getCustomerDeliveryDate(customerId: string): Observable<string> {
        return this.httpService.get$(this.apiService.pathUrl.customerDeliveryDate(customerId));
    }
}
